import type { ReactNode, ChangeEvent } from 'react';
import { useEffect, useRef, useState, useCallback } from 'react';

import type { IconName } from '@similarweb/icons';
import { SWReactIcons } from '@similarweb/icons';

import { useOnClickOutside } from '../../hooks/useOnClickOutside';
import { translate } from '../../services/i18n';
import { DotsLoader } from '../DotsLoader';

import {
  StyledDropdownContainer,
  StyledSearchField,
  StyledSearchInput,
  StyledSeparator,
  StyledDropdownScrollContainer,
  StyledDropdownFooterButton,
} from './styles';

const animateAppearance = (element: HTMLElement, type: 'appearing' | 'disappearing'): Animation => {
  const keyframes = type === 'appearing' ? [{ opacity: 0 }, { opacity: 1 }] : [{ opacity: 1 }, { opacity: 0 }];
  return element.animate(keyframes, { duration: 400, easing: 'ease-out', fill: 'forwards' });
};

export type Placement = 'left' | 'right' | 'on-top-left' | 'on-top-right' | 'on-bottom-left' | 'on-bottom-right';

export type DropdownProps = {
  children: ReactNode;
  isOpened: boolean;
  onClose: () => void;
  onCloseFinish?: () => void;
  className?: string;
  searchProps?: {
    value: string;
    onChange: (event: ChangeEvent<HTMLInputElement>) => void;
    placeholder?: string;
    isLoading?: boolean;
    iconName?: IconName;
  };
  placement?: Placement;
  minWidth?: number | string;
  maxHeight?: number;
  isFooterButtonEnabled?: boolean;
  onFooterButtonClick?: () => void;
};

export const Dropdown = ({
  children,
  onClose,
  isOpened,
  searchProps,
  onCloseFinish,
  className,
  placement = 'left',
  minWidth,
  maxHeight,
  isFooterButtonEnabled,
  onFooterButtonClick,
}: DropdownProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [internalIsOpened, setInternalIsOpened] = useState(isOpened);

  useEffect(() => {
    if (!isOpened) {
      return;
    }

    searchInputRef?.current?.focus();
  }, [isOpened]);

  const handleClickOutside = useCallback(
    (event: MouseEvent | KeyboardEvent) => {
      onClose();
      event.stopPropagation();
    },
    [onClose]
  );

  useOnClickOutside(containerRef, handleClickOutside, isOpened);

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    if (isOpened) {
      animateAppearance(containerRef.current, 'appearing');
      setInternalIsOpened(true);
      return;
    }

    const animation = animateAppearance(containerRef.current, 'disappearing');
    animation.onfinish = () => {
      setInternalIsOpened(false);
      onCloseFinish?.();
    };
  }, [isOpened, onCloseFinish]);

  if (!isOpened && !internalIsOpened) {
    return null;
  }

  return (
    <StyledDropdownContainer
      data-automation="Dropdown"
      ref={containerRef}
      className={className}
      placement={placement}
      minWidth={minWidth}
    >
      {searchProps ? (
        <>
          <StyledSearchField>
            <SWReactIcons iconName={searchProps.iconName ?? 'search'} size="sm" />
            <StyledSearchInput type="text" {...searchProps} ref={searchInputRef} />
            {searchProps.isLoading ? <DotsLoader /> : null}
          </StyledSearchField>
          <StyledSeparator />
        </>
      ) : null}
      <StyledDropdownScrollContainer maxHeight={maxHeight}>{children}</StyledDropdownScrollContainer>
      {isFooterButtonEnabled ? (
        <StyledDropdownFooterButton onClick={onFooterButtonClick}>
          {translate('common.apply')}
        </StyledDropdownFooterButton>
      ) : null}
    </StyledDropdownContainer>
  );
};
